import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { QuoteRow } from "@/components/QuoteRow";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

export default function QuoteHistory({ open, onOpenChange, onReopen }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch {
      toast.error("Could not load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  }, []);

  const setStatus = useCallback(async (id, status) => {
    setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/quotes/${id}`, { status });
    } catch {
      load();
      toast.error("Could not update status");
    }
  }, [load]);

  const reopen = useCallback(
    (q) => {
      onReopen(q.data);
      onOpenChange(false);
      toast.success("Quote reopened");
    },
    [onReopen, onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1C1917]">Saved Quotes</DialogTitle>
          <DialogDescription>Reopen, update status, download the PDF, email a quote, or delete saved quotes.</DialogDescription>
        </DialogHeader>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`${API}/quotes/export.csv`, "_blank")}
            className="self-start border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED]"
            data-testid="export-quotes-csv"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        <div className="overflow-auto flex-1 space-y-2" data-testid="quote-history-list">
          {loading && <p className="text-sm text-[#57534E]">Loading…</p>}
          {!loading && quotes.length === 0 && (
            <p className="text-sm text-[#57534E]">No saved quotes yet.</p>
          )}
          {quotes.map((q) => (
            <QuoteRow key={q.id} q={q} onStatusChange={setStatus} onReopen={reopen} onDelete={remove} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
